// Packages
import { createAsyncThunk } from '@reduxjs/toolkit';
// API
import { TodoAPI } from 'api/endpoints';
// Interfaces and types
import {
  IGetTodoRes,
  ICreateTodoBody,
  ICreateTodoRes,
  IGetEmployeeTodoBody,
  IGetEmployeeTodoRes,
  IGetTodoDetailsRes,
  IUpdateTodoRes,
  IDeleteTodoRes,
  ITodoParams,
  IUpdateTodoParams,
  IShiftTodoParams,
  IUpdateTasksParams,
  ICreateTodoListBody,
  ICreateTodoListRes,
  IUpdateTodoListParams,
  ITodoListDetails,
  ITodoListDays,
  ITodoListDayParams,
  ITodoListDay,
  IFixTodoTaskParams,
  IGetTodoTodayRes,
  ITodoResultsRefresh,
} from 'types/todoTypes';
import { IErrorRes, TQueryParams, IResMessage } from 'types/appTypes';

const getTodoAsync = createAsyncThunk<IGetTodoRes, TQueryParams, { rejectValue: IErrorRes }>(
  'todo/getTodo',
  async (params: TQueryParams, { rejectWithValue }) => {
    try {
      return await TodoAPI.getTodo(params);
    } catch (error) {
      return rejectWithValue(error as IErrorRes);
    }
  },
);

const createTodoAsync = createAsyncThunk<
  ICreateTodoRes,
  ICreateTodoBody,
  { rejectValue: IErrorRes }
>('todo/createTodo', async (cred: ICreateTodoBody, { rejectWithValue }) => {
  try {
    return await TodoAPI.createTodo(cred);
  } catch (error) {
    return rejectWithValue(error as IErrorRes);
  }
});

const getEmployeeTodoAsync = createAsyncThunk<
  IGetEmployeeTodoRes,
  IGetEmployeeTodoBody,
  { rejectValue: IErrorRes }
>('todo/getEmployeeTodo', async (cred: IGetEmployeeTodoBody, { rejectWithValue }) => {
  try {
    return await TodoAPI.getEmployeeTodo(cred);
  } catch (error) {
    return rejectWithValue(error as IErrorRes);
  }
});

const getTodoDetailsAsync = createAsyncThunk<
  IGetTodoDetailsRes,
  number,
  { rejectValue: IErrorRes }
>('todo/getTodoDetails', async (id: number, { rejectWithValue }) => {
  try {
    return await TodoAPI.getTodoDetails(id);
  } catch (error) {
    return rejectWithValue(error as IErrorRes);
  }
});

const updateTodoAsync = createAsyncThunk<
  IUpdateTodoRes,
  IUpdateTodoParams,
  { rejectValue: IErrorRes }
>('todo/updateTodo', async (cred: IUpdateTodoParams, { rejectWithValue }) => {
  try {
    return await TodoAPI.updateTodo(cred);
  } catch (error) {
    return rejectWithValue(error as IErrorRes);
  }
});

const deleteTodoAsync = createAsyncThunk<
  IDeleteTodoRes,
  ITodoParams,
  { rejectValue: IErrorRes }
>('todo/deleteTodo', async (params: ITodoParams, { rejectWithValue }) => {
  try {
    return await TodoAPI.deleteTodo(params);
  } catch (error) {
    return rejectWithValue(error as IErrorRes);
  }
});

const deleteShiftTodoAsync = createAsyncThunk<
  IDeleteTodoRes,
  IShiftTodoParams,
  { rejectValue: IErrorRes }
>('todo/deleteShiftTodo', async (params: IShiftTodoParams, { rejectWithValue }) => {
  try {
    return await TodoAPI.deleteShiftTodo(params);
  } catch (error) {
    return rejectWithValue(error as IErrorRes);
  }
});

const updateJobTodoTasksStatusAsync = createAsyncThunk<
  IUpdateTodoRes,
  IUpdateTasksParams,
  { rejectValue: IErrorRes }
>('todo/updateJobTodoTasksStatus', async (cred: IUpdateTasksParams, { rejectWithValue }) => {
  try {
    return await TodoAPI.updateTasksStatus(cred);
  } catch (error) {
    return rejectWithValue(error as IErrorRes);
  }
});

const createTodoListAsync = createAsyncThunk<
  ICreateTodoListRes,
  ICreateTodoListBody,
  { rejectValue: IErrorRes }
>('todo/createTodoList', async (body: ICreateTodoListBody, { rejectWithValue }) => {
  try {
    return await TodoAPI.createTodoList(body);
  } catch (error) {
    return rejectWithValue(error as IErrorRes);
  }
});

const getTodoListDetailsAsync = createAsyncThunk<
  ITodoListDetails,
  number,
  { rejectValue: IErrorRes }
>('todo/getTodoListDetails', async (id: number, { rejectWithValue }) => {
  try {
    return await TodoAPI.getTodoListDetails(id);
  } catch (error) {
    return rejectWithValue(error as IErrorRes);
  }
});

const updateTodoListAsync = createAsyncThunk<
  IResMessage,
  IUpdateTodoListParams,
  { rejectValue: IErrorRes }
>('todo/updateTodoList', async (params: IUpdateTodoListParams, { rejectWithValue }) => {
  try {
    return await TodoAPI.updateTodoList(params);
  } catch (error) {
    return rejectWithValue(error as IErrorRes);
  }
});

const getTodoListDaysAsync = createAsyncThunk<
  ITodoListDays,
  number,
  { rejectValue: IErrorRes }
>('todo/getTodoListDays', async (id: number, { rejectWithValue }) => {
  try {
    return await TodoAPI.getTodoListDays(id);
  } catch (error) {
    return rejectWithValue(error as IErrorRes);
  }
});

const getTodoListDayAsync = createAsyncThunk<
  ITodoListDay,
  ITodoListDayParams,
  { rejectValue: IErrorRes }
>('todo/getTodoListDay', async (params: ITodoListDayParams, { rejectWithValue }) => {
  try {
    return await TodoAPI.getTodoListDay(params);
  } catch (error) {
    return rejectWithValue(error as IErrorRes);
  }
});

const fixTodoListDayTaskAsync = createAsyncThunk<
  IResMessage,
  IFixTodoTaskParams,
  { rejectValue: IErrorRes }
>('todo/fixTodoListDayTask', async (params: IFixTodoTaskParams, { rejectWithValue }) => {
  try {
    return await TodoAPI.fixTodoListDayTask(params);
  } catch (error) {
    return rejectWithValue(error as IErrorRes);
  }
});

const getTodoTodayAsync = createAsyncThunk<
  IGetTodoTodayRes,
  TQueryParams,
  { rejectValue: IErrorRes }
>('todo/getTodoToday', async (params: TQueryParams, { rejectWithValue }) => {
  try {
    return await TodoAPI.getTodoToday(params);
  } catch (error) {
    return rejectWithValue(error as IErrorRes);
  }
});

// A reload that leaves the page as it is: no loader, nothing cleared. For a tick made in place, so the
// list shows who ticked and when without blinking.
const refreshTodoResultsAsync = createAsyncThunk<
  ITodoResultsRefresh,
  number,
  { rejectValue: IErrorRes }
>('todo/refreshTodoResults', async (id: number, { rejectWithValue }) => {
  try {
    const [details, list] = await Promise.all([
      TodoAPI.getTodoDetails(id),
      TodoAPI.getTodoListDetails(id),
    ]);

    return { details, list };
  } catch (error) {
    return rejectWithValue(error as IErrorRes);
  }
});

export {
  refreshTodoResultsAsync,
  fixTodoListDayTaskAsync,
  getTodoTodayAsync,
  createTodoListAsync,
  getTodoListDetailsAsync,
  updateTodoListAsync,
  getTodoListDaysAsync,
  getTodoListDayAsync,
  getTodoAsync,
  createTodoAsync,
  getEmployeeTodoAsync,
  getTodoDetailsAsync,
  updateTodoAsync,
  deleteTodoAsync,
  deleteShiftTodoAsync,
  updateJobTodoTasksStatusAsync,
};
